import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Profile, ConsentVersion } from '@/types/database';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  latestConsent: ConsentVersion | null;
  loading: boolean;
  needsLgpdConsent: boolean;
  acceptLgpd: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
  return data;
}

async function fetchLatestConsent(): Promise<ConsentVersion | null> {
  const { data } = await supabase
    .from('consents_versions')
    .select('*')
    .order('published_at', { ascending: false })
    .limit(1)
    .single();
  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [latestConsent, setLatestConsent] = useState<ConsentVersion | null>(null);
  const [loading, setLoading] = useState(true);
  // Guarda o id do user atualmente autenticado. Usado dentro do listener de
  // onAuthStateChange para distinguir um SIGNED_IN real (login novo) de um
  // SIGNED_IN "falso" disparado pelo supabase-js ao retomar foco da aba — esse
  // segundo caso precisa ser tratado como TOKEN_REFRESHED (sem mexer em
  // loading, ou o ProtectedRoute desmonta a árvore inteira).
  const lastUserIdRef = useRef<string | null>(null);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  }, [user]);

  const loadUserData = useCallback(async (u: User) => {
    const [p, c] = await Promise.all([fetchProfile(u.id), fetchLatestConsent()]);
    setProfile(p);
    setLatestConsent(c);
  }, []);

  useEffect(() => {
    // Timeout de segurança: se loadUserData travar, desbloqueamos após 8s
    const safetyTimeout = setTimeout(() => setLoading(false), 8000);

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      lastUserIdRef.current = s?.user?.id ?? null;
      if (s?.user) {
        loadUserData(s.user).finally(() => {
          clearTimeout(safetyTimeout);
          setLoading(false);
        });
      } else {
        clearTimeout(safetyTimeout);
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      // CRÍTICO: SIGNED_IN pode ser disparado pelo supabase-js não apenas em
      // login real, mas também quando a aba volta a ter foco e a sessão foi
      // renovada em background. TOKEN_REFRESHED também dispara periodicamente.
      // Setar loading=true em qualquer desses casos faz o ProtectedRoute
      // trocar o <Outlet/> pelo spinner e DESMONTA toda a árvore abaixo —
      // perdendo o estado de modais/formulários abertos. Por isso só
      // disparamos loading quando o user de fato MUDOU (id diferente do
      // último que conhecíamos).
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLatestConsent(null);
        lastUserIdRef.current = null;
        setLoading(false);
        return;
      }

      const incomingUserId = s?.user?.id ?? null;
      const isSameUser = incomingUserId !== null && incomingUserId === lastUserIdRef.current;

      // Login real (user mudou): precisa recarregar profile.
      if (event === 'SIGNED_IN' && !isSameUser) {
        setSession(s);
        setUser(s?.user ?? null);
        lastUserIdRef.current = incomingUserId;
        if (s?.user) {
          setLoading(true);
          loadUserData(s.user).finally(() => setLoading(false));
        }
        return;
      }

      // Demais casos (TOKEN_REFRESHED, USER_UPDATED, INITIAL_SESSION,
      // SIGNED_IN com mesmo user): só atualiza session/user em segundo
      // plano. Profile continua válido; queries refetch sob demanda.
      setSession(s);
      setUser(s?.user ?? null);
      lastUserIdRef.current = incomingUserId;
    });

    return () => {
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [loadUserData]);

  const needsLgpdConsent = Boolean(
    session &&
    latestConsent &&
    (!profile?.lgpd_accepted_at || profile.lgpd_version !== latestConsent.version),
  );

  const acceptLgpd = useCallback(async () => {
    if (!user || !latestConsent) return;
    await supabase
      .from('profiles')
      .update({
        lgpd_accepted_at: new Date().toISOString(),
        lgpd_version: latestConsent.version,
      })
      .eq('id', user.id);
    await refreshProfile();
  }, [user, latestConsent, refreshProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        latestConsent,
        loading,
        needsLgpdConsent,
        acceptLgpd,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
